import * as React from 'react';
import {AppState, Platform} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {HubConnectionBuilder, LogLevel} from '@microsoft/signalr';
import {getVehicleslist} from '../../actions/vehicleslist';
import {getVehicle} from '../../actions/vehicle';
import {getModal} from '../../actions/modal';
import {getUser} from '../../actions/user';
import {getApp} from '../../actions/app';
import {showSheet} from '../../actions/SheetActions';
import SyncTime from 'src/imports/sync-time';
import RestClock from 'src/services/clock/RestClock';
import {network} from 'src/constants/config';
import {selecetusertype} from '../../selectors/User';
import {selecetvehivletype} from '../../selectors/Vehicle';
import {selecetApptype} from '../../selectors/App';
import {useTranslation} from 'react-i18next';
import {useMutation} from 'react-query';
import {useApi} from '../../providers/ApiProvider';

export default function useTripSync() {
  const dispatch = useDispatch();
  const {token} = useSelector(state => selecetusertype(state));
  const {t} = useTranslation();
  const {api} = useApi();
  const appState = React.useRef(AppState.currentState);
  const pollingInterval = React.useRef(null);
  const connectionRef = React.useRef(null);
  const isConnected = React.useRef(false);
  const isInitialConnection = React.useRef(0);
  const reconnectCount = React.useRef(0);
  const {vehicle, tripId, reserveId} = useSelector(state =>
    selecetvehivletype(state),
  );
  const {selectedVehicle} = useSelector(state => selecetApptype(state));

  // --------------------------
  // Mutation برای وضعیت سفر
  // --------------------------
  const dashboardMyTripsMutation = useMutation({
    mutationFn: () => api.myActiveTrip(),
    onSuccess: response => {
      const data = response?.Data;

      if (data?.ActiveTrip) {
        dispatch(
          getVehicle({
            activeSeconds: data?.ActiveTrip?.ActiveSeconds,
            pauseSeconds: data?.ActiveTrip?.PauseSeconds,
            stop: data?.ActiveTrip?.LastDetailStatus === 'pause',
            activeMinutes: Math.round(data?.ActiveTrip?.ActiveSeconds / 60),
            pauseMinutes: Math.round(data?.ActiveTrip?.PauseSeconds / 60),
            money: data?.ActiveTrip?.Price,
          }),
        );
      } else {
        // وقتی ActiveTrip خالی است، باتم‌ها و وضعیت سفر را ریست کن
        // فقط اگر وسیله رزرو یا سلکت نبود

        if (!reserveId && !selectedVehicle) {
          dispatch(getUser({hasTrip: false}));
          dispatch(
            getVehicle({
              activeSeconds: 0,
              pauseSeconds: 0,
              activeMinutes: 0,
              pauseMinutes: 0,
              stop: false,
              money: 0,
              vehicle: null,
              tripId: null,
            }),
          );
          dispatch(getVehicleslist({liveActiveVehicle: null, marker: null}));
        }
      }
    },
  });

  // --------------------------
  // Polling وضعیت سفر
  // --------------------------
  const startPolling = () => {
    if (!token) return;
    dashboardMyTripsMutation.mutate();
    if (pollingInterval.current) clearInterval(pollingInterval.current);
    pollingInterval.current = setInterval(() => {
      dashboardMyTripsMutation.mutate();
    }, 30000); // هر 30 ثانیه
  };

  const stopPolling = () => {
    if (pollingInterval.current) clearInterval(pollingInterval.current);
  };
  // --------------------------
  // مدیریت visibility وب (به جای focus/blur)
  // --------------------------
  React.useEffect(() => {
    if (
      Platform.OS === 'web' &&
      typeof document !== 'undefined' &&
      typeof document.addEventListener === 'function'
    ) {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          startPolling();
          startSignalR();
          // small delay ensures modal shows reliably
          setTimeout(() => {
            // اگر modal بخواد فوراً نمایش داده بشه، dispatch مربوطه اینجا
          }, 100); // 100 میلی‌ثانیه کافی است
        } else {
          stopPolling();
          stopSignalR();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // اگر صفحه الان visible است، شروع کن
      if (document.visibilityState === 'visible') {
        handleVisibilityChange();
      }

      return () => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
        stopPolling();
      };
    }

    return undefined;
  }, [token]);

  // --------------------------
  // مدیریت foreground/background موبایل
  // --------------------------
  React.useEffect(() => {
    if (Platform.OS !== 'web') {
      const subscription = AppState.addEventListener('change', nextAppState => {
        if (
          appState.current.match(/inactive|background/) &&
          nextAppState === 'active' &&
          isInitialConnection.current === 1

        ) {
          startPolling();
          startSignalR();

        } else if (nextAppState.match(/inactive|background/)) {
          stopPolling();
          stopSignalR();
        }
        appState.current = nextAppState;
      });

      return () => subscription.remove();
    }
  }, [token]);

  // --------------------------
  // SignalR
  // --------------------------
  const startSignalR = React.useCallback(async () => {
    if (!token || !network?.baseUrl) return;
    if (connectionRef.current && isConnected.current) return;
    const connection = new HubConnectionBuilder()
      .withUrl(`${network.baseUrl}/messages`, {accessTokenFactory: () => token})
      .configureLogging(LogLevel.None)
      .withAutomaticReconnect([0, 3000, 10000, 30000, 60000])
      .build();

    // --- Event Handlers ---
    connection.on('EndTripByAdmin', model => {
      dispatch(showSheet({sheetId: ''}));
      dispatch(getUser({hasTrip: false}));
      dispatch(
        getVehicle({
          activeSeconds: 0,
          pauseSeconds: 0,
          activeMinutes: 0,
          pauseMinutes: 0,
          stop: false,
          vehicle: null, // ← این خط وسیله را پاک می‌کند
          tripId: null, // ← این خط شناسه سفر را پاک می‌کند
          reserveId: null,
          reservetime: null,
        }),
      );
      dispatch(
        getVehicleslist({type: 'both', liveActiveVehicle: null, marker: null}),
      );
      dispatch(
        getApp({
          selectedVehicle: undefined,
        }),
      );
      dispatch(
        getModal({
          modal: true,
          image: 'endTripIcon',
          width: 88,
          height: 60,
          Notification: true,
          func: 'tripended',
          number: t('admin_end_trip'),
        }),
      );
      dispatch(getUser({notificationmodal: true}));
    });

    connection.on('CancelTripByAdmin', model => {
      dispatch(showSheet({sheetId: ''}));
      dispatch(getUser({hasTrip: false}));
      dispatch(
        getVehicle({
          activeSeconds: 0,
          pauseSeconds: 0,
          activeMinutes: 0,
          pauseMinutes: 0,
          stop: false,
          vehicle: null, // ← این خط وسیله را پاک می‌کند
          tripId: null, // ← این خط شناسه سفر را پاک می‌کند
          reserveId: null,
          reservetime: null,
        }),
      );

      dispatch(
        getVehicleslist({type: 'both', liveActiveVehicle: null, marker: null}),
      );
      dispatch(
        getApp({
          selectedVehicle: undefined,
        }),
      );
      dispatch(
        getModal({
          modal: true,
          image: 'endTripIcon',
          width: 88,
          height: 60,
          Notification: true,
          func: 'cancelTripByAdmin',
          number: t('admin_cancel_trip'),
        }),
      );
      dispatch(getUser({notificationmodal: true}));
    });

    connection.on('UserStatus', model =>
      dispatch(getUser({userStatus: model})),
    );
    connection.on('UpdateActiveTripStatusApp', model =>
      dispatch(getVehicle({adminCommand: model})),
    );

    connection.onreconnecting(error => {
      reconnectCount.current += 1;
      isConnected.current = false;
      console.log('SignalR reconnecting...', reconnectCount.current, error);
    });

    connection.onreconnected(connectionId => {
      reconnectCount.current = 0;
      isConnected.current = true;
      connection.invoke('GetTime').then(date => SyncTime(dispatch, date));
    });

    connection.onclose(error => {
      isConnected.current = false;
      RestClock(dispatch);
    });

    try {
      await connection.start();
      isConnected.current = true;
      connectionRef.current = connection;
      connection.invoke('GetTime').then(date => SyncTime(dispatch, date));
      console.log('✅ SignalR connected successfully');
    } catch (error) {
      console.log('❌ SignalR connect failed', error);
      RestClock(dispatch);
      isConnected.current = false;
      setTimeout(() => startSignalR(), 5000);
    }
  }, [token, dispatch, t]);

  const stopSignalR = () => {
    if (connectionRef.current) {
      connectionRef.current.stop();
      isConnected.current = false;
      connectionRef.current = null;
    }
  };

  // --------------------------
  // شروع اولیه
  // --------------------------
  React.useEffect(() => {
    startPolling();
    startSignalR();
    isInitialConnection.current = 1;
    return () => {
      stopPolling();
      stopSignalR();
    };
  }, [startSignalR]);

  return null;
}
